import { z } from 'zod';
import { GATEWAY_CONFIG_PUSH_STATUSES, } from './gateway-config-push-keys.js';
import { PeripheralProfileIdentitySchema } from './peripheral-profile-identity.js';
// ── Component config (§6) ───────────────────────────────────────────────────
export const EfemOrchestratorConfigSchema = z.object({
    efemAdapterUrl: z.string().url(),
    efemAdapterTimeout: z.number().int().positive().default(30000),
    attestationGuardBaseUrl: z.string().url().default('http://127.0.0.1:8791'),
    attestationGuardTimeoutMs: z.number().int().positive().default(1500),
    heartbeatIntervalMs: z.number().int().positive().default(60000),
    features: z.object({
        barcodeScanner: z.boolean().default(true),
        waferidCamera: z.boolean().default(true),
    }).default({ barcodeScanner: true, waferidCamera: true }),
});
export const EfemBarcodeScannerProfileSchema = z.object({
    usbVendorId: z.string().min(1),
    usbProductId: z.string().min(1),
    /** When `cloud-resolve`, edge skips read-serial + Guard; cloud resolves Cnapse serial from gateway assignment. */
    identity: PeripheralProfileIdentitySchema.optional(),
});
export const EfemBarcodeScannerConfigSchema = z.object({
    profiles: z.array(EfemBarcodeScannerProfileSchema).min(1),
    baudRate: z.number().int().positive().default(9600),
    delimiter: z.string().default('\r'),
    reconnectIntervalMs: z.number().int().positive().default(5000),
    debounceMs: z.number().int().nonnegative().default(2000),
    discoveryPollIntervalMs: z.number().int().positive().default(30000),
    commandScanTimeoutMs: z.number().int().positive().default(10000),
});
/** Handheld pilot — same VCP/serial shape as fixed mount until Rakinda S2 VID/PID is confirmed. */
export const EfemHandheldBarcodeScannerConfigSchema = EfemBarcodeScannerConfigSchema;
export const EfemWaferIdCameraProfileSchema = z.object({
    host: z.string().min(1),
    port: z.number().int().min(1).max(65535),
});
export const EfemInitialLightingSchema = z.object({
    mode: z.number().int().min(0).max(14),
    power: z.number().int().min(0).max(127),
});
/** S3 defaults for `runAutoAdjustLightPower` when a command omits `lightingPlan.autoAdjust`. */
export const EfemWaferIdAutoAdjustConfigSchema = z.object({
    targetGray: z.number().int().min(0).max(255).default(225),
    tolerance: z.number().int().positive().default(20),
    maxIterations: z.number().int().min(1).max(32).default(10),
});
/** S3 defaults for grossly-bad image checks when a command omits `lightingPlan.imageQualityOptions`. */
export const EfemWaferIdImageQualityConfigSchema = z.object({
    saturationThreshold: z.number().int().min(0).max(255).optional().default(240),
    saturationPercentageLimit: z.number().optional().default(20),
    darkPercentile: z.number().min(0).max(1).optional().default(0.99),
    darkGrayLimit: z.number().int().min(0).max(255).optional().default(20),
    blankImageTolerance: z.number().int().positive().optional().default(3),
});
export const EfemWaferIdCaptureProtocolSchema = z.enum(['telnet', 'insight1069']).default('telnet');
export const EfemWaferIdCameraConfigSchema = z.object({
    profiles: z.array(EfemWaferIdCameraProfileSchema).min(1),
    username: z.string().default('admin'),
    password: z.string().default(''),
    timeout: z.number().int().positive().default(10000),
    simulatorMode: z.boolean().default(false),
    discoveryPollIntervalMs: z.number().int().positive().default(30000),
    reconnectIntervalMs: z.number().int().positive().default(5000),
    /** `telnet` = port 23 + RB (default). `insight1069` = hybrid telnet commands + 1069 image stream. */
    captureProtocol: EfemWaferIdCaptureProtocolSchema,
    /** TCP port for `insight1069` greyscale stream (Cognex default 1069). Commands use telnet `profiles[].port`. */
    insightPort: z.number().int().positive().default(1069),
    /** Job file on camera flash; used when preparing the camera before a 1069 session. */
    jobFilename: z.string().default('WafID450.job'),
    initialLighting: EfemInitialLightingSchema.default({ mode: 1, power: 64 }),
    autoAdjust: EfemWaferIdAutoAdjustConfigSchema.default({
        targetGray: 225,
        tolerance: 20,
        maxIterations: 10,
    }),
    imageQualityOptions: EfemWaferIdImageQualityConfigSchema.default({}),
});
export const EfemAdapterKeyFieldSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9._-]*$/)
    .transform((s) => s.toLowerCase());
export const EfemAdapterConnectionSchema = z.object({
    serverUrl: z.string().optional(),
    callbackMachineIp: z.string().optional(),
    callbackPort: z.number().int().optional(),
    baseUrl: z.string().optional(),
    dataDirectory: z.string().optional(),
}).passthrough();
export const EfemAdapterRestSchema = z.object({
    listenHost: z.string().default('0.0.0.0'),
    listenPort: z.number().int().min(1).max(65535).default(8088),
    basePath: z.string().default('/api'),
});
export const EfemAdapterConfigSchema = z.object({
    efemAdapterKey: EfemAdapterKeyFieldSchema,
    adapterDisplayName: z.string().optional(),
    connection: EfemAdapterConnectionSchema.default({}),
    rest: EfemAdapterRestSchema.default({}),
    timeouts: z.object({
        operationMs: z.number().int().positive().default(30000),
        healthMs: z.number().int().positive().default(5000),
    }).default({}),
});
// ── Manifest / reload (EFEM-specific policies; base shape in gateway-config-manifest-schemas) ──
import { GatewayConfigManifestComponentSchema, GatewayConfigManifestSchema, ApplyGatewayConfigCommandSchema, } from './gateway-config-manifest-schemas.js';
export const EfemReloadPolicySchema = z.enum([
    'none',
    'orchestrator-hot',
    'manager-restart',
    'adapter-service-restart',
    'adapter-swap',
    'orchestrator-restart',
]);
export const EfemManifestComponentSchema = GatewayConfigManifestComponentSchema.extend({
    reloadPolicy: EfemReloadPolicySchema,
    contentSha256: z.string(),
});
export const EfemGatewayManifestSchema = GatewayConfigManifestSchema.extend({
    presignedUrlExpiresAt: z.string().datetime(),
    pushedBy: z.object({
        userId: z.string(),
        email: z.string().email().or(z.literal('')),
    }),
    components: z.record(z.string(), EfemManifestComponentSchema),
});
export const ApplyEfemGatewayConfigPayloadSchema = ApplyGatewayConfigCommandSchema.extend({
    manifestKey: z.string(),
});
// ── Overrides (partial JSON per peripheral) ─────────────────────────────────
export const EfemGatewayOverridesSchema = z.object({
    orchestrator: EfemOrchestratorConfigSchema.partial().optional(),
    'barcode-scanner': EfemBarcodeScannerConfigSchema.partial().optional(),
    'handheld-barcode-scanner': EfemHandheldBarcodeScannerConfigSchema.partial().optional(),
    'waferid-camera': EfemWaferIdCameraConfigSchema.partial().optional(),
    'efem-adapter': EfemAdapterConfigSchema.partial().optional(),
}).strict();
export const EfemMergedGatewayConfigSchema = z.object({
    orchestrator: EfemOrchestratorConfigSchema,
    barcodeScanner: EfemBarcodeScannerConfigSchema,
    handheldBarcodeScanner: EfemHandheldBarcodeScannerConfigSchema,
    waferidCamera: EfemWaferIdCameraConfigSchema,
    efemAdapter: EfemAdapterConfigSchema,
    appConfigXml: z.string().nullable(),
});
// ── Provider API ────────────────────────────────────────────────────────────
export const EfemGatewaySerialParamSchema = z.object({
    serialNumber: z.string().min(1).max(64),
});
export const EfemGatewayConfigOverridesBodySchema = z.object({
    overrides: EfemGatewayOverridesSchema,
    siteId: z.string().uuid().optional(),
});
export const EfemGatewayConfigPushBodySchema = z.object({
    overrides: EfemGatewayOverridesSchema.optional(),
    siteId: z.string().uuid().optional(),
});
export const EfemGatewayConfigPreviewResponseSchema = z.object({
    merged: EfemMergedGatewayConfigSchema,
    reloadPlan: z.array(z.string()),
    warnings: z.array(z.string()),
    adapterSwap: z.object({
        previousEfemAdapterKey: z.string().nullable(),
        newEfemAdapterKey: z.string(),
    }).nullable(),
});
export const EfemGatewayConfigPushResultSchema = z.object({
    configVersion: z.string(),
    manifestKey: z.string(),
    status: z.enum(GATEWAY_CONFIG_PUSH_STATUSES),
    mqttRequestId: z.string().uuid().optional(),
    warnings: z.array(z.string()),
    reloadPlan: z.array(z.string()),
    presignedUrlExpiresAt: z.string().datetime(),
    pushedAt: z.string().datetime(),
});
export const EfemGatewayConfigHistoryItemSchema = z.object({
    configVersion: z.string(),
    status: z.enum(GATEWAY_CONFIG_PUSH_STATUSES),
    pushedAt: z.string().datetime(),
    pushedBy: z.object({ userId: z.string(), email: z.string() }).optional(),
    mqttSentAt: z.string().datetime().optional(),
    mqttAckAt: z.string().datetime().optional(),
    errorMessage: z.string().optional(),
    pushMetadata: z.record(z.unknown()).optional(),
    edgeResponse: z.record(z.unknown()).optional(),
});
// ── GET overrides response ───────────────────────────────────────────────────
export const EfemGatewayOverridesResponseSchema = z.object({
    success: z.literal(true),
    data: EfemGatewayOverridesSchema,
});
/** Layer 1 registry template (same Zod parse path as config preview merge base). */
export const EfemGatewayRegistryTemplateDataSchema = z.object({
    efemAdapterKey: z.string(),
    merged: EfemMergedGatewayConfigSchema,
});
export const EfemGatewayRegistryTemplateResponseSchema = z.object({
    success: z.literal(true),
    data: EfemGatewayRegistryTemplateDataSchema,
});
/** GET /provider/gateways/:serialNumber/push/:version/config (EFEM only). */
export const EfemGatewayConfigVersionConfigResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        merged: EfemMergedGatewayConfigSchema,
        warnings: z.array(z.string()),
        reloadPlan: z.array(z.string()),
        adapterSwap: z.object({
            previousEfemAdapterKey: z.string(),
            newEfemAdapterKey: z.string(),
        }).nullable(),
    }),
});
//# sourceMappingURL=efem-gateway-config-schemas.js.map